/**
 * Calendar block: interactive booking calendar with available time slots.
 *
 * Fetches real availability data from the backend API, renders a date strip
 * and time slot list. Selecting a slot dispatches a book: action to trigger
 * the booking flow form.
 *
 * Multi-location: if the workspace has locations, shows a location picker
 * first. Selecting a location fetches that location's availability.
 */

import { registerBlockRenderer, getBlockRendererContext } from './registry';
import { escapeHtml, createBlockWrapper, createErrorState } from './utils';
import type { CalendarBlockData, CalendarDay, CalendarSlot, CalendarLocation, CalendarMeetingType } from './types';
import { inlineLoaderHTML } from '../../core/loading/activity-loader';

// =============================================================================
// CALENDAR BLOCK
// =============================================================================

registerBlockRenderer('calendar', (data: unknown, container: HTMLElement, onAction?: (value: string) => void) => {
  const calData = data as CalendarBlockData;
  const ctx = getBlockRendererContext();

  const wrapper = createBlockWrapper('calendar');
  const vertical = ctx.vertical;

  // =========================================================================
  // VERTICAL DECORATORS
  // =========================================================================

  // Restaurant: reservation stamp (TABLE #)
  if (vertical === 'restaurant') {
    const stamp = document.createElement('div');
    stamp.className = 'pms-calendar-reservation-stamp';
    stamp.textContent = 'TABLE #' + Math.floor(Math.random() * 900 + 100);
    wrapper.prepend(stamp);
  }

  // SaaS: traffic-light dots
  if (vertical === 'saas') {
    const dots = document.createElement('div');
    dots.className = 'pms-calendar-terminal-dots';
    wrapper.prepend(dots);
  }

  // Ecommerce: bag handle icon
  if (vertical === 'ecommerce') {
    const bag = document.createElement('div');
    bag.className = 'pms-calendar-bag-icon';
    wrapper.prepend(bag);
  }

  // Healthcare: medical cross
  if (vertical === 'healthcare') {
    const cross = document.createElement('div');
    cross.className = 'pms-calendar-medical-cross';
    wrapper.prepend(cross);
  }

  // Finance: vault badge with pulse dot
  if (vertical === 'finance') {
    const badge = document.createElement('div');
    badge.className = 'pms-calendar-vault-badge';
    badge.textContent = 'SECURED';
    wrapper.prepend(badge);
  }

  // Real estate: compass crosshair
  if (vertical === 'real_estate') {
    const compass = document.createElement('div');
    compass.className = 'pms-calendar-compass';
    wrapper.prepend(compass);
  }

  // Portfolio: CMY color swatches
  if (vertical === 'portfolio') {
    const dots = document.createElement('div');
    dots.className = 'pms-calendar-studio-dots';
    wrapper.prepend(dots);
  }

  // Automotive: gauge badge + redline indicator
  if (vertical === 'automotive') {
    const badge = document.createElement('div');
    badge.className = 'pms-calendar-gauge-badge';
    badge.textContent = 'BOOK';
    wrapper.prepend(badge);
    const redline = document.createElement('div');
    redline.className = 'pms-calendar-redline-dot';
    wrapper.prepend(redline);
  }

  // Professional services: seal emblem
  if (vertical === 'professional_services') {
    const seal = document.createElement('div');
    seal.className = 'pms-calendar-seal';
    wrapper.prepend(seal);
  }

  // Dental: appointment badge + availability dot
  if (vertical === 'dental') {
    const badge = document.createElement('div');
    badge.className = 'pms-calendar-appt-badge';
    badge.innerHTML =
      `<span class="pms-calendar-appt-badge-label">APPT</span>` +
      `<span class="pms-calendar-appt-badge-ref">APT-${Math.floor(Math.random() * 9000 + 1000)}</span>`;
    wrapper.prepend(badge);

    const dot = document.createElement('div');
    dot.className = 'pms-calendar-availability-dot';
    wrapper.prepend(dot);
  }

  // Agency: campaign badge
  if (vertical === 'agency') {
    const badge = document.createElement('div');
    badge.className = 'pms-calendar-campaign-badge';
    badge.textContent = 'BRIEF';
    wrapper.prepend(badge);
  }

  // Education: lesson badge
  if (vertical === 'education') {
    const badge = document.createElement('div');
    badge.className = 'pms-calendar-lesson-badge';
    badge.textContent = 'CLASS #' + Math.floor(Math.random() * 900 + 100);
    wrapper.prepend(badge);
  }

  // Nonprofit: impact badge
  if (vertical === 'nonprofit') {
    const badge = document.createElement('div');
    badge.className = 'pms-calendar-impact-badge';
    badge.innerHTML =
      `<span class="pms-calendar-impact-heart">\u2665</span>` +
      `<span>SCHEDULE</span>`;
    wrapper.prepend(badge);
  }

  // Legal: case scheduling badge
  if (vertical === 'legal') {
    const badge = document.createElement('div');
    badge.className = 'pms-calendar-case-badge';
    badge.textContent = 'DOCKET #' + Math.floor(Math.random() * 9000 + 1000);
    wrapper.prepend(badge);
  }

  // Fun: hacker terminal status + blinking dot
  if (vertical === 'fun') {
    const status = document.createElement('div');
    status.className = 'pms-calendar-monitor-status';
    status.textContent = 'SCHEDULE';
    wrapper.prepend(status);
    const dot = document.createElement('div');
    dot.className = 'pms-calendar-monitor-dot';
    wrapper.prepend(dot);
  }

  // Title
  const title = document.createElement('div');
  title.className = 'pms-calendar-title';
  title.textContent = calData.title || 'Book an Appointment';
  wrapper.appendChild(title);

  // Location picker container (hidden if no locations)
  const locationStrip = document.createElement('div');
  locationStrip.className = 'pms-calendar-location-strip';
  locationStrip.style.display = 'none';
  wrapper.appendChild(locationStrip);

  // Meeting type picker container (hidden if no meeting types)
  const meetingTypeStrip = document.createElement('div');
  meetingTypeStrip.className = 'pms-calendar-meeting-type-strip';
  meetingTypeStrip.style.display = 'none';
  wrapper.appendChild(meetingTypeStrip);

  // Party size picker container (hidden unless meeting type has collectPartySize)
  const partySizeContainer = document.createElement('div');
  partySizeContainer.className = 'pms-calendar-party-size';
  partySizeContainer.style.display = 'none';
  wrapper.appendChild(partySizeContainer);

  // Date strip container
  const dateStrip = document.createElement('div');
  dateStrip.className = 'pms-calendar-date-strip';
  wrapper.appendChild(dateStrip);

  // Slots container with loading state
  const slotsContainer = document.createElement('div');
  slotsContainer.className = 'pms-calendar-slots';
  slotsContainer.innerHTML = `<div class="pms-calendar-loading">${inlineLoaderHTML('Loading availability...')}</div>`;
  wrapper.appendChild(slotsContainer);

  container.appendChild(wrapper);

  // =========================================================================
  // STATE
  // =========================================================================

  let days: CalendarDay[] = [];
  let selectedDayIndex = 0;
  let timezone = calData.timezone || '';
  let bookingFlowId = calData.bookingFlowId || '';
  let locations: CalendarLocation[] = [];
  let selectedLocationId: string | null = null;
  let meetingTypes: CalendarMeetingType[] = [];
  let selectedMeetingTypeId: string | null = calData.selectedMeetingTypeId || null;
  let partySize = 1;

  // =========================================================================
  // LOCATION PICKER
  // =========================================================================

  function renderLocationStrip(): void {
    locationStrip.innerHTML = '';
    if (locations.length === 0) {
      locationStrip.style.display = 'none';
      return;
    }

    locationStrip.style.display = '';

    locations.forEach((loc) => {
      const btn = document.createElement('button');
      btn.className = `pms-calendar-location-btn${loc.id === selectedLocationId ? ' pms-calendar-location-active' : ''}`;
      btn.innerHTML = `<span class="pms-calendar-location-name">${escapeHtml(loc.name)}</span>`;
      if (loc.address) {
        btn.innerHTML += `<span class="pms-calendar-location-address">${escapeHtml(loc.address)}</span>`;
      }

      btn.addEventListener('click', () => {
        if (loc.id === selectedLocationId) return;
        selectedLocationId = loc.id;
        renderLocationStrip();
        // Show loading and fetch slots for selected location
        dateStrip.innerHTML = '';
        slotsContainer.innerHTML = `<div class="pms-calendar-loading">${inlineLoaderHTML('Loading availability...')}</div>`;
        fetchSlotsForLocation(loc.id);
      });

      locationStrip.appendChild(btn);
    });
  }

  // =========================================================================
  // MEETING TYPE PICKER
  // =========================================================================

  function renderMeetingTypeStrip(): void {
    meetingTypeStrip.innerHTML = '';
    if (meetingTypes.length <= 1) {
      // 0 or 1 meeting type: no picker needed (auto-selected)
      meetingTypeStrip.style.display = 'none';
      return;
    }

    meetingTypeStrip.style.display = '';

    meetingTypes.forEach((mt) => {
      const btn = document.createElement('button');
      btn.className = `pms-calendar-meeting-type-btn${mt.id === selectedMeetingTypeId ? ' pms-calendar-meeting-type-active' : ''}`;

      // Apply the meeting type's accent color as a CSS variable
      if (mt.color) {
        btn.style.setProperty('--meeting-type-color', mt.color);
      }

      const nameSpan = `<span class="pms-calendar-meeting-type-name">${escapeHtml(mt.name)}</span>`;
      const durationSpan = `<span class="pms-calendar-meeting-type-duration">${mt.durationMinutes} min</span>`;
      btn.innerHTML = `${nameSpan}${durationSpan}`;

      btn.addEventListener('click', () => {
        if (mt.id === selectedMeetingTypeId) return;
        selectedMeetingTypeId = mt.id;
        partySize = 1; // Reset party size on meeting type change
        renderMeetingTypeStrip();
        renderPartySizePicker();
        // Show loading and re-fetch slots for selected meeting type
        dateStrip.innerHTML = '';
        slotsContainer.innerHTML = `<div class="pms-calendar-loading">${inlineLoaderHTML('Loading availability...')}</div>`;
        refetchSlots();
      });

      meetingTypeStrip.appendChild(btn);
    });
  }

  // =========================================================================
  // PARTY SIZE PICKER
  // =========================================================================

  function getSelectedMeetingType(): CalendarMeetingType | null {
    if (!selectedMeetingTypeId) return null;
    return meetingTypes.find(mt => mt.id === selectedMeetingTypeId) || null;
  }

  function renderPartySizePicker(): void {
    partySizeContainer.innerHTML = '';
    const mt = getSelectedMeetingType();
    if (!mt || !mt.collectPartySize) {
      partySizeContainer.style.display = 'none';
      partySize = 1;
      return;
    }

    partySizeContainer.style.display = '';
    const maxCap = mt.maxCapacityPerSlot || 20;

    const label = document.createElement('span');
    label.className = 'pms-calendar-party-size-label';
    label.textContent = 'Party size';
    partySizeContainer.appendChild(label);

    const controls = document.createElement('div');
    controls.className = 'pms-calendar-party-size-controls';

    const minusBtn = document.createElement('button');
    minusBtn.className = 'pms-calendar-party-size-btn';
    minusBtn.textContent = '\u2212';
    minusBtn.type = 'button';

    const valueDisplay = document.createElement('span');
    valueDisplay.className = 'pms-calendar-party-size-value';
    valueDisplay.textContent = String(partySize);

    const plusBtn = document.createElement('button');
    plusBtn.className = 'pms-calendar-party-size-btn';
    plusBtn.textContent = '+';
    plusBtn.type = 'button';

    function updateDisplay(): void {
      valueDisplay.textContent = String(partySize);
      minusBtn.disabled = partySize <= 1;
      plusBtn.disabled = partySize >= maxCap;
    }

    minusBtn.addEventListener('click', () => {
      if (partySize > 1) { partySize--; updateDisplay(); refetchSlots(); }
    });

    plusBtn.addEventListener('click', () => {
      if (partySize < maxCap) { partySize++; updateDisplay(); refetchSlots(); }
    });

    controls.appendChild(minusBtn);
    controls.appendChild(valueDisplay);
    controls.appendChild(plusBtn);
    partySizeContainer.appendChild(controls);

    updateDisplay();
  }

  /**
   * Re-fetch slots after a meeting type selection change.
   * Delegates to location-specific or general fetch depending on state.
   */
  async function refetchSlots(): Promise<void> {
    if (selectedLocationId) {
      await fetchSlotsForLocation(selectedLocationId);
    } else {
      await fetchSlotsGeneral();
    }
  }

  // =========================================================================
  // FETCH LOCATIONS + AVAILABILITY
  // =========================================================================

  function applyDays(newDays: CalendarDay[]): void {
    days = newDays;
    if (days.length === 0) {
      slotsContainer.innerHTML = '<div class="pms-calendar-empty">No availability configured</div>';
      dateStrip.innerHTML = '';
      return;
    }
    selectedDayIndex = 0;
    for (let i = 0; i < days.length; i++) {
      if (days[i]!.slots.length > 0) {
        selectedDayIndex = i;
        break;
      }
    }
    renderDateStrip();
    renderSlots();
  }

  async function fetchSlotsForLocation(locationId: string): Promise<void> {
    if (!ctx.apiClient || !ctx.workspaceId) {
      slotsContainer.innerHTML = createErrorState('Scheduling is not available right now');
      return;
    }

    try {
      const numDays = Math.min(14, Math.max(1, 5));
      const today = new Date().toISOString().split('T')[0];
      const baseUrl = ctx.apiClient.getBaseUrl();
      let url = `${baseUrl}/api/workspaces/${ctx.workspaceId}/calendar/available-slots?date=${today}&days=${numDays}&locationId=${locationId}`;
      if (selectedMeetingTypeId) {
        url += `&meetingTypeId=${encodeURIComponent(selectedMeetingTypeId)}`;
      }
      if (partySize > 1) {
        url += `&partySize=${partySize}`;
      }

      const response = await fetch(url, { cache: 'no-store' });
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const result = await response.json();
      timezone = result.timezone || timezone;
      if (result.bookingFlowId) bookingFlowId = result.bookingFlowId;
      applyDays(result.days || []);
    } catch (_err) {
      slotsContainer.innerHTML = createErrorState('Availability could not be loaded right now');
    }
  }

  /**
   * Fetch workspace-level slots (no specific location).
   * Called from initial fetchSlots() and from refetchSlots() after meeting type change.
   */
  async function fetchSlotsGeneral(): Promise<void> {
    if (!ctx.apiClient || !ctx.workspaceId) {
      slotsContainer.innerHTML = createErrorState('Scheduling is not available right now');
      return;
    }

    try {
      const numDays = Math.min(14, Math.max(1, 5));
      const today = new Date().toISOString().split('T')[0];
      const baseUrl = ctx.apiClient.getBaseUrl();
      let url = `${baseUrl}/api/workspaces/${ctx.workspaceId}/calendar/available-slots?date=${today}&days=${numDays}`;
      if (selectedMeetingTypeId) {
        url += `&meetingTypeId=${encodeURIComponent(selectedMeetingTypeId)}`;
      }
      if (partySize > 1) {
        url += `&partySize=${partySize}`;
      }

      const response = await fetch(url, { cache: 'no-store' });
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const result = await response.json();
      timezone = result.timezone || timezone;
      if (result.bookingFlowId) bookingFlowId = result.bookingFlowId;
      applyDays(result.days || []);
    } catch (_err) {
      slotsContainer.innerHTML = createErrorState('Availability could not be loaded right now');
    }
  }

  async function fetchSlots(): Promise<void> {
    // If static days data was provided (e.g. demo/components page), use it directly
    if (calData.days && calData.days.length > 0) {
      timezone = calData.timezone || timezone;
      // Apply static meeting types if provided
      if (calData.meetingTypes && calData.meetingTypes.length > 0) {
        meetingTypes = calData.meetingTypes;
        if (!selectedMeetingTypeId) selectedMeetingTypeId = meetingTypes[0]!.id;
        renderMeetingTypeStrip();
        renderPartySizePicker();
      }
      applyDays(calData.days);
      return;
    }

    if (!ctx.apiClient || !ctx.workspaceId) {
      slotsContainer.innerHTML = createErrorState('Scheduling is not available right now');
      return;
    }

    try {
      const baseUrl = ctx.apiClient.getBaseUrl();

      // Fetch meeting types if not provided
      if (!calData.meetingTypes) {
        try {
          const mtUrl = `${baseUrl}/api/workspaces/${ctx.workspaceId}/calendar/meeting-types`;
          const mtResponse = await fetch(mtUrl, { cache: 'no-store' });
          if (mtResponse.ok) {
            const mtData: CalendarMeetingType[] = await mtResponse.json();
            if (mtData.length > 0) {
              meetingTypes = mtData;
              if (!selectedMeetingTypeId) selectedMeetingTypeId = mtData[0]!.id;
              renderMeetingTypeStrip();
              renderPartySizePicker();
            }
          }
        } catch {
          // Meeting types fetch failure is non-fatal — continue without them
        }
      } else if (calData.meetingTypes.length > 0) {
        meetingTypes = calData.meetingTypes;
        if (!selectedMeetingTypeId) selectedMeetingTypeId = meetingTypes[0]!.id;
        renderMeetingTypeStrip();
        renderPartySizePicker();
      }

      // Check for locations
      const locUrl = `${baseUrl}/api/workspaces/${ctx.workspaceId}/calendar/locations`;
      const locResponse = await fetch(locUrl, { cache: 'no-store' });

      if (locResponse.ok) {
        const locData: CalendarLocation[] = await locResponse.json();
        if (locData.length > 0) {
          // Multi-location workspace: show location picker, auto-select first
          locations = locData;
          selectedLocationId = locData[0]!.id;
          renderLocationStrip();
          await fetchSlotsForLocation(selectedLocationId);
          return;
        }
      }

      // No locations — fetch workspace-level slots directly
      await fetchSlotsGeneral();
    } catch (_err) {
      slotsContainer.innerHTML = createErrorState('Availability could not be loaded right now');
    }
  }

  // =========================================================================
  // RENDER DATE STRIP
  // =========================================================================

  function renderDateStrip(): void {
    dateStrip.innerHTML = '';

    days.forEach((day, index) => {
      const btn = document.createElement('button');
      btn.className = `pms-calendar-day-btn${index === selectedDayIndex ? ' pms-calendar-day-active' : ''}`;

      const hasSlots = day.slots.length > 0;
      if (!hasSlots) btn.classList.add('pms-calendar-day-unavailable');

      const dateParts = day.date.split('-');
      const dayNum = parseInt(dateParts[2] || '0', 10);

      btn.innerHTML = `
        <span class="pms-calendar-day-name">${escapeHtml(day.dayName)}</span>
        <span class="pms-calendar-day-num">${dayNum}</span>
        ${hasSlots ? `<span class="pms-calendar-day-count">${day.slots.length}</span>` : ''}
      `;

      btn.addEventListener('click', () => {
        if (!hasSlots) return;
        selectedDayIndex = index;
        renderDateStrip();
        renderSlots();
      });

      dateStrip.appendChild(btn);
    });
  }

  // =========================================================================
  // RENDER TIME SLOTS
  // =========================================================================

  function renderSlots(): void {
    slotsContainer.innerHTML = '';

    const day = days[selectedDayIndex];
    if (!day || day.slots.length === 0) {
      slotsContainer.innerHTML = '<div class="pms-calendar-empty">No available slots on this day</div>';
      return;
    }

    const grid = document.createElement('div');
    grid.className = 'pms-calendar-slots-grid';

    day.slots.forEach((slot: CalendarSlot, idx: number) => {
      const btn = document.createElement('button');
      btn.className = 'pms-calendar-slot-btn';
      btn.style.setProperty('--slot-index', String(idx));
      btn.textContent = formatTime(slot.startTime);
      btn.title = `${formatTime(slot.startTime)} - ${formatTime(slot.endTime)}`;

      btn.addEventListener('click', () => {
        if (onAction) {
          // Format: book:{start}|{end}|{flowId}|{locationId}|{meetingTypeId}|{partySize}
          const locPart = selectedLocationId || '';
          const mtPart = selectedMeetingTypeId || '';
          const psPart = partySize > 1 ? String(partySize) : '';
          onAction(`book:${slot.startTime}|${slot.endTime}|${bookingFlowId}|${locPart}|${mtPart}|${psPart}`);
        }
      });

      grid.appendChild(btn);
    });

    slotsContainer.appendChild(grid);

    // Timezone note
    if (timezone) {
      const tzNote = document.createElement('div');
      tzNote.className = 'pms-calendar-timezone';
      tzNote.textContent = `Times shown in ${timezone.replace(/_/g, ' ')}`;
      slotsContainer.appendChild(tzNote);
    }
  }

  // =========================================================================
  // HELPERS
  // =========================================================================

  function formatTime(isoString: string): string {
    try {
      const date = new Date(isoString);
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: true });
    } catch {
      return isoString;
    }
  }

  // Fetch on render
  fetchSlots();
});
